import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Star, Pin, Pencil, Eye, Share2, MoreVertical, Copy, Trash2, RotateCcw,
  Check, Loader2, CloudOff, X, Plus, Bell, Tag as TagIcon, UserPlus, Clock,
  Bold, Italic, Strikethrough, Code, Heading1, Heading2, Heading3,
  List, ListOrdered, ListTodo, Link2, Quote, Info,
} from 'lucide-react'
import {
  Button, Input, Textarea, Badge, Switch, Label,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  Popover, PopoverTrigger, PopoverContent,
} from '@goodday-clone/ui'
import { cn } from '@/shared/utils/utils'
import { useAuth } from '@/context/AuthContext'
import { useUserOptions } from '@/shared/hooks/useUserOptions'
import {
  useNote, useNoteFolders, useNoteCollaborators, useNoteReminder,
  useDeleteNote, useDeleteNotePermanent, useRestoreNote, useDuplicateNote,
  notesApi,
} from './notepad/notesApi'
import type { Note, NotePriority, NoteVisibility } from './notepad/notesApi'
import { authorName, departmentTagStyle, formatFullDateTime, initialsOf, PRIORITY_LABELS, VISIBILITY_LABELS } from './notepad/notesApi'
import { renderMarkdown, toggleTaskInSource, applyMarkdownAction } from './notepad/markdown'
import type { MarkdownAction } from './notepad/markdown'

type SaveState = 'saved' | 'saving' | 'error'

const TOOLBAR: { action: MarkdownAction; icon: any; label: string }[] = [
  { action: 'bold', icon: Bold, label: 'Bold' },
  { action: 'italic', icon: Italic, label: 'Italic' },
  { action: 'strike', icon: Strikethrough, label: 'Strikethrough' },
  { action: 'code', icon: Code, label: 'Inline code' },
  { action: 'h1', icon: Heading1, label: 'Heading 1' },
  { action: 'h2', icon: Heading2, label: 'Heading 2' },
  { action: 'h3', icon: Heading3, label: 'Heading 3' },
  { action: 'bullet', icon: List, label: 'Bullet list' },
  { action: 'numbered', icon: ListOrdered, label: 'Numbered list' },
  { action: 'checklist', icon: ListTodo, label: 'Checklist' },
  { action: 'link', icon: Link2, label: 'Link' },
  { action: 'quote', icon: Quote, label: 'Quote' },
]

export default function NoteDetailPage() {
  const { noteId } = useParams<{ noteId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()

  const noteQuery = useNote(noteId)
  const note = noteQuery.data?.data
  const canEdit = !!noteQuery.data?.canEdit && !note?.deletedAt

  const isPrivileged = user?.role === 'SUPERADMIN' || user?.role === 'ADMIN'
  const isOwner = !!note && note.authorId && (typeof note.authorId === 'object' ? note.authorId._id === user?.id : note.authorId === user?.id)

  const [editing, setEditing] = useState(false)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [saveState, setSaveState] = useState<SaveState>('saved')
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [newTag, setNewTag] = useState('')

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const titleRef = useRef<HTMLInputElement>(null)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const loadedId = useRef<string | null>(null)

  // Seed the editor once per note load
  useEffect(() => {
    if (!note || loadedId.current === note._id) return
    loadedId.current = note._id
    setTitle(note.title)
    setContent(note.content)
    setSaveState('saved')
    if ((location.state as any)?.openShare) setDetailsOpen(true)
    if ((location.state as any)?.focusTitle && note.title === 'Untitled note') {
      setEditing(true)
      setTimeout(() => {
        titleRef.current?.focus()
        titleRef.current?.select()
      }, 50)
    }
  }, [note, location.state])

  const scheduleSave = useCallback((patch: Record<string, any>) => {
    if (!noteId) return
    setSaveState('saving')
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(async () => {
      try {
        await notesApi.update(noteId, patch)
        setSaveState('saved')
        noteQuery.refetch()
      } catch {
        setSaveState('error')
      }
    }, 1000)
  }, [noteId, noteQuery])

  const saveNow = useCallback(async (patch: Record<string, any>) => {
    if (!noteId) return
    if (saveTimer.current) clearTimeout(saveTimer.current)
    setSaveState('saving')
    try {
      await notesApi.update(noteId, patch)
      setSaveState('saved')
      noteQuery.refetch()
    } catch {
      setSaveState('error')
    }
  }, [noteId, noteQuery])

  const onTitleChange = (v: string) => { setTitle(v); scheduleSave({ title: v.trim() || 'Untitled note' }) }
  const onContentChange = (v: string) => { setContent(v); scheduleSave({ content: v }) }

  const applyToolbar = (action: MarkdownAction) => {
    const el = textareaRef.current
    if (!el) return
    const { value, selectionStart, selectionEnd } = applyMarkdownAction(action, el.value, el.selectionStart, el.selectionEnd)
    setContent(value)
    scheduleSave({ content: value })
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(selectionStart, selectionEnd)
    })
  }

  const previewHtml = useMemo(() => renderMarkdown(content), [content])

  const onPreviewClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (target.tagName !== 'INPUT' || !(target as HTMLInputElement).type) return
    e.preventDefault()
    if (!canEdit) return
    const idx = parseInt((target as HTMLInputElement).dataset.taskIndex || '', 10)
    if (isNaN(idx)) return
    const next = toggleTaskInSource(content, idx)
    setContent(next)
    saveNow({ content: next })
  }

  const goBack = () => {
    if (window.history.length > 1) navigate(-1)
    else navigate('/app/notepad')
  }

  if (noteQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    )
  }

  if (noteQuery.isError || !note) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-slate-50 text-center gap-3">
        <CloudOff className="h-12 w-12 text-slate-300" />
        <h3 className="text-base font-semibold text-slate-900">Note not found</h3>
        <p className="text-sm text-slate-500">It may have been deleted or you no longer have access to it.</p>
        <Button variant="outline" size="sm" onClick={goBack}>Back to All Notes</Button>
      </div>
    )
  }

  const folder = typeof note.folderId === 'object' ? note.folderId : null

  return (
    <div className="flex h-full min-h-0 bg-slate-50/60">
      {/* Editor column */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Editor header */}
        <div className="bg-white border-b border-slate-200 px-4 lg:px-6 py-3 flex items-center gap-2">
          <button onClick={goBack} className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-emerald-700 mr-2 shrink-0">
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back to All Notes</span>
          </button>
          <span className="flex-1" />
          <SaveStateIndicator state={saveState} />
          <button onClick={() => note && saveNow({ isStarred: !note.isStarred })} className="p-2 rounded-lg hover:bg-slate-100" title={note.isStarred ? 'Unstar' : 'Star'}>
            <Star className={cn('h-4 w-4', note.isStarred ? 'fill-amber-400 text-amber-400' : 'text-slate-400')} />
          </button>
          <button onClick={() => saveNow({ isPinned: !note.isPinned })} className="p-2 rounded-lg hover:bg-slate-100" title={note.isPinned ? 'Unpin' : 'Pin'}>
            <Pin className={cn('h-4 w-4', note.isPinned ? 'fill-emerald-600 text-emerald-600' : 'text-slate-400')} />
          </button>
          {canEdit && (
            <Button variant="outline" size="sm" className="gap-1.5 rounded-lg border-slate-200 h-8" onClick={() => setEditing((v) => !v)}>
              {editing ? <Eye className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
              {editing ? 'Preview' : 'Edit'}
            </Button>
          )}
          <Button variant="outline" size="sm" className="gap-1.5 rounded-lg border-slate-200 h-8" onClick={() => setDetailsOpen(true)}>
            <Share2 className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Share</span>
          </Button>
          <NoteActionsMenu
            note={note}
            isOwner={isOwner || isPrivileged}
            onDeleted={goBack}
          />
          <button
            onClick={() => setDetailsOpen(true)}
            className="xl:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-500"
            title="Note details"
          >
            <Info className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 lg:px-8 py-6">
            {note.deletedAt && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3 text-sm text-amber-800">
                <Trash2 className="h-4 w-4 shrink-0" />
                <span className="flex-1">This note is in the trash and read-only.</span>
                <RestoreAndPurge note={note} onDeleted={goBack} />
              </div>
            )}

            {/* Title */}
            {editing ? (
              <Input
                ref={titleRef}
                value={title}
                onChange={(e) => onTitleChange(e.target.value)}
                placeholder="Note title"
                className="text-2xl font-bold border-0 px-0 focus-visible:ring-0 placeholder:text-slate-300 text-slate-900"
              />
            ) : (
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Pin className="h-5 w-5 text-emerald-600 fill-emerald-600 shrink-0" />
                {note.title}
              </h1>
            )}

            {/* Metadata row */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {note.department && (
                <span className={cn('px-2.5 py-1 rounded-full text-xs font-medium border', departmentTagStyle(note.department))}>
                  {note.department}
                </span>
              )}
              {note.priority && (
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-rose-50 text-rose-600 border border-rose-200">
                  {PRIORITY_LABELS[note.priority]}
                </span>
              )}
              {note.tags?.map((t) => (
                <span key={t} className="px-2.5 py-1 rounded-full text-xs font-medium bg-sky-50 text-sky-700 border border-sky-200">{t}</span>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
              <span className="flex items-center gap-1.5">
                <span className="h-5 w-5 rounded-full bg-slate-700 text-white text-[9px] font-semibold flex items-center justify-center">
                  {initialsOf(authorName(note, user?.id))}
                </span>
                Created by {authorName(note, user?.id)}
              </span>
              <span>·</span>
              <span>Created {formatFullDateTime(note.createdAt)}</span>
              <span>·</span>
              <span>Last updated {formatFullDateTime(note.updatedAt)}</span>
              {folder && (
                <>
                  <span>·</span>
                  <span className="flex items-center gap-1 text-emerald-700">{folder.name}</span>
                </>
              )}
            </div>

            <div className="mt-5 border-t border-slate-200" />

            {/* Content */}
            {editing ? (
              <div className="mt-4">
                <div className="flex flex-wrap items-center gap-0.5 sticky top-0 z-10 bg-white pb-2 -mt-2 pt-2">
                  {TOOLBAR.map((t) => (
                    <button
                      key={t.action}
                      onClick={() => applyToolbar(t.action)}
                      className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-800"
                      title={t.label}
                    >
                      <t.icon className="h-4 w-4" />
                    </button>
                  ))}
                </div>
                <Textarea
                  ref={textareaRef}
                  value={content}
                  onChange={(e) => onContentChange(e.target.value)}
                  placeholder="Write your note… Markdown supported: # heading, **bold**, - list, - [ ] task, > quote"
                  className="min-h-[420px] text-[15px] leading-relaxed border-0 px-0 focus-visible:ring-0 resize-y font-sans"
                />
              </div>
            ) : (
              <div
                onClick={onPreviewClick}
                className="mt-5 text-[15px] [&_h1]:text-xl [&_h2]:text-lg [&_h3]:text-base"
                dangerouslySetInnerHTML={{ __html: previewHtml.html || '<p class="text-slate-400 italic">This note is empty. Hit Edit to start writing.</p>' }}
              />
            )}
          </div>
        </div>
      </main>

      {/* Desktop details panel */}
      <aside className="hidden xl:flex w-80 shrink-0 border-l border-slate-200 bg-white overflow-y-auto">
        <NoteDetailsPanel
          note={note}
          canEdit={canEdit}
          canManage={isOwner || isPrivileged}
          isPrivileged={isPrivileged}
          onPatch={saveNow}
          currentUserId={user?.id}
          currentUserName={user?.name}
        />
      </aside>

      {/* Details drawer — opens at every viewport width; on xl+ the same
          content is also docked in the side panel, but Share must give
          visible feedback. */}
      {detailsOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setDetailsOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-xl overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
              <p className="font-semibold text-slate-900 text-sm">Note Details</p>
              <button onClick={() => setDetailsOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="h-4 w-4" />
              </button>
            </div>
            <NoteDetailsPanel
              note={note}
              canEdit={canEdit}
              canManage={isOwner || isPrivileged}
              isPrivileged={isPrivileged}
              onPatch={saveNow}
              currentUserId={user?.id}
              currentUserName={user?.name}
            />
          </div>
        </div>
      )}
    </div>
  )
}

function SaveStateIndicator({ state }: { state: SaveState }) {
  if (state === 'saving') {
    return <span className="text-xs text-slate-400 flex items-center gap-1 mr-1"><Loader2 className="h-3 w-3 animate-spin" /> Saving…</span>
  }
  if (state === 'error') {
    return <span className="text-xs text-rose-600 flex items-center gap-1 mr-1"><CloudOff className="h-3 w-3" /> Save failed — retrying on next change</span>
  }
  return <span className="text-xs text-emerald-600 flex items-center gap-1 mr-1"><Check className="h-3 w-3" /> Saved</span>
}

function NoteActionsMenu({ note, isOwner, onDeleted }: { note: Note; isOwner: boolean; onDeleted: () => void }) {
  const duplicate = useDuplicateNote()
  const trash = useDeleteNote()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem className="flex items-center gap-2 text-sm" onClick={() => duplicate.mutate(note._id, { onSuccess: (copy) => window.open(`/app/notepad/${copy._id}`, '_blank') })}>
          <Copy className="h-3.5 w-3.5" /> Duplicate
        </DropdownMenuItem>
        {isOwner && !note.deletedAt && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="flex items-center gap-2 text-sm text-red-600" onClick={() => trash.mutate(note._id, { onSuccess: onDeleted })}>
              <Trash2 className="h-3.5 w-3.5" /> Move to trash
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function RestoreAndPurge({ note, onDeleted }: { note: Note; onDeleted: () => void }) {
  const restore = useRestoreNote()
  const purge = useDeleteNotePermanent()
  return (
    <span className="flex items-center gap-2">
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => restore.mutate(note._id)}>
        <RotateCcw className="h-3 w-3 mr-1" /> Restore
      </Button>
      <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => purge.mutate(note._id, { onSuccess: onDeleted })}>
        Delete forever
      </Button>
    </span>
  )
}

// ── Right details panel ──────────────────────────────────────────────────────

interface PanelProps {
  note: Note
  canEdit: boolean
  canManage: boolean
  isPrivileged: boolean
  onPatch: (patch: Record<string, any>) => Promise<void>
  currentUserId?: string
  currentUserName?: string
}

function Section({ title, icon: Icon, action, children }: { title: string; icon: any; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="px-4 py-4 border-b border-slate-100">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[13px] font-semibold text-slate-900 flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 text-slate-400" /> {title}
        </p>
        {action}
      </div>
      {children}
    </div>
  )
}

function NoteDetailsPanel({ note, canEdit, canManage, isPrivileged, onPatch, currentUserId, currentUserName }: PanelProps) {
  const foldersQuery = useNoteFolders()
  const folders = foldersQuery.data || []
  const collaborators = useNoteCollaborators()
  const reminder = useNoteReminder()
  const [tagInput, setTagInput] = useState('')

  const [reminderDate, setReminderDate] = useState(note.reminder?.at ? new Date(note.reminder.at).toISOString().slice(0, 10) : '')
  const [reminderTime, setReminderTime] = useState(note.reminder?.at ? new Date(note.reminder.at).toTimeString().slice(0, 5) : '09:00')
  useEffect(() => {
    if (note.reminder?.at) {
      const d = new Date(note.reminder.at)
      setReminderDate(d.toISOString().slice(0, 10))
      setReminderTime(d.toTimeString().slice(0, 5))
    }
  }, [note.reminder?.at])

  const saveReminder = (enabled: boolean, date = reminderDate, time = reminderTime) => {
    if (!enabled) {
      reminder.mutate({ id: note._id, enabled: false })
      return
    }
    // Auto-set today's date if enabling reminder without a date
    const finalDate = date || new Date().toISOString().slice(0, 10)
    if (finalDate !== date) setReminderDate(finalDate)
    reminder.mutate({ id: note._id, enabled: true, at: new Date(`${finalDate}T${time || '09:00'}`).toISOString() })
  }

  const addTag = () => {
    const t = tagInput.trim()
    if (!t || note.tags?.includes(t)) { setTagInput(''); return }
    setTagInput('')
    onPatch({ tags: [...(note.tags || []), t].slice(0, 20) })
  }
  const removeTag = (t: string) => onPatch({ tags: (note.tags || []).filter((x) => x !== t) })

  const visibilityOptions: NoteVisibility[] = ['PRIVATE', 'SHARED', 'DEPARTMENT', ...(isPrivileged ? ['ORGANIZATION' as NoteVisibility] : [])]

  return (
    <div className="flex-1">
      <Section title="Note Details" icon={Info}>
        <dl className="space-y-2.5 text-xs">
          <DetailRow label="Created" value={formatFullDateTime(note.createdAt)} />
          <DetailRow label="Last Updated" value={formatFullDateTime(note.updatedAt)} />
          <DetailRow label="Created By" value={authorName(note, currentUserId)} />
          <DetailRow label="Visibility" value={VISIBILITY_LABELS[note.visibility || 'PRIVATE']} />
        </dl>
        {canEdit && (
          <div className="mt-3 space-y-2">
            <div>
              <Label className="text-[11px] text-slate-500 mb-1 block">Folder</Label>
              <Select
                value={typeof note.folderId === 'object' ? note.folderId?._id || 'none' : (note.folderId || 'none')}
                onValueChange={(v) => onPatch({ folderId: v === 'none' ? null : v })}
              >
                <SelectTrigger className="h-8 text-xs w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unfiled</SelectItem>
                  {folders.map((f) => <SelectItem key={f._id} value={f._id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-slate-500 mb-1 block">Priority</Label>
              <Select
                value={note.priority || 'none'}
                onValueChange={(v) => onPatch({ priority: v === 'none' ? null : (v as NotePriority) })}
              >
                <SelectTrigger className="h-8 text-xs w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No priority</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="LOW">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </Section>

      <Section
        title="Collaborators"
        icon={UserPlus}
        action={canManage && <CollaboratorPicker note={note} onAdd={(userId, access) => collaborators.add.mutate({ id: note._id, userId, access })} currentUserId={currentUserId} />}
      >
        <div className="space-y-2">
          <CollaboratorRow
            name={authorName(note) || currentUserName || 'You'}
            department={note.authorId && typeof note.authorId === 'object' ? note.authorId.department : undefined}
            accessLabel="Owner"
            dot
          />
          {(note.collaborators || []).map((c) => {
            const u = typeof c.userId === 'string' ? null : c.userId
            const userId = u ? u._id : (c.userId as string)
            return (
              <CollaboratorRow
                key={userId}
                name={u?.name || 'Unknown'}
                department={u?.department}
                accessLabel={c.access === 'EDIT' ? 'Can edit' : 'Can view'}
                onRemove={canManage ? () => collaborators.remove.mutate({ id: note._id, userId }) : undefined}
                onAccessChange={canManage ? (access) => collaborators.add.mutate({ id: note._id, userId, access }) : undefined}
              />
            )
          })}
          {!(note.collaborators || []).length && (
            <p className="text-xs text-slate-400">
              {note.visibility === 'PRIVATE' ? 'Only you can access this note.' : 'Shared via note permissions below.'}
            </p>
          )}
        </div>
      </Section>

      <Section
        title="Reminder"
        icon={Bell}
        action={
          <Switch
            checked={!!note.reminder?.enabled}
            disabled={!canEdit}
            onCheckedChange={(checked) => saveReminder(checked)}
          />
        }
      >
        {note.reminder?.enabled ? (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                type="date"
                value={reminderDate}
                disabled={!canEdit}
                onChange={(e) => { setReminderDate(e.target.value); saveReminder(true, e.target.value, reminderTime) }}
                className="h-8 text-xs flex-1"
              />
              <Input
                type="time"
                value={reminderTime}
                disabled={!canEdit}
                onChange={(e) => { setReminderTime(e.target.value); if (reminderDate) saveReminder(true, reminderDate, e.target.value) }}
                className="h-8 text-xs w-24"
              />
            </div>
            <p className="text-[11px] text-slate-400 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {note.reminder.sentAt ? 'Reminder notification sent.' : "You'll get a reminder notification"}
            </p>
          </div>
        ) : (
          <p className="text-xs text-slate-400">Turn on to get notified about this note.</p>
        )}
      </Section>

      <Section title="Tags" icon={TagIcon}>
        <div className="flex flex-wrap gap-1.5">
          {(note.tags || []).map((t) => (
            <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-sky-50 text-sky-700 border border-sky-200">
              {t}
              {canEdit && (
                <button onClick={() => removeTag(t)} className="hover:text-sky-900">
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
          {!note.tags?.length && <p className="text-xs text-slate-400">No tags yet.</p>}
        </div>
        {canEdit && (
          <div className="mt-2 flex gap-1.5">
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag() } }}
              placeholder="Add tag"
              className="h-7 text-xs"
              maxLength={40}
            />
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={addTag} disabled={!tagInput.trim()}>
              <Plus className="h-3 w-3" />
            </Button>
          </div>
        )}
      </Section>

      <Section title="Permissions" icon={Share2}>
        {canManage ? (
          <Select value={note.visibility || 'PRIVATE'} onValueChange={(v) => onPatch({ visibility: v })}>
            <SelectTrigger className="h-8 text-xs w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {visibilityOptions.map((o) => <SelectItem key={o} value={o}>{VISIBILITY_LABELS[o]}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <p className="text-xs text-slate-500">{VISIBILITY_LABELS[note.visibility || 'PRIVATE']}</p>
        )}
        <p className="text-[11px] text-slate-400 mt-2">
          {(note.visibility || 'PRIVATE') === 'PRIVATE' ? 'Only you can access this note.' : 'Confidential notes should stay private or shared with selected users only.'}
        </p>
      </Section>
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-slate-400 shrink-0">{label}</dt>
      <dd className="text-slate-700 font-medium text-right">{value}</dd>
    </div>
  )
}

function CollaboratorRow({ name, department, accessLabel, dot, onRemove, onAccessChange }: {
  name: string
  department?: string
  accessLabel: string
  dot?: boolean
  onRemove?: () => void
  onAccessChange?: (access: 'VIEW' | 'EDIT') => void
}) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="relative h-7 w-7 rounded-full bg-slate-700 text-white text-[10px] font-semibold flex items-center justify-center shrink-0">
        {initialsOf(name)}
        {dot && <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-500 border border-white" />}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-slate-900 truncate">{name}</p>
        <p className="text-[11px] text-slate-400">{accessLabel}{department ? ` · ${department}` : ''}</p>
      </div>
      {onAccessChange && (
        <select
          value={accessLabel === 'Can edit' ? 'EDIT' : 'VIEW'}
          onChange={(e) => onAccessChange(e.target.value as 'VIEW' | 'EDIT')}
          className="text-[11px] border border-slate-200 rounded-md px-1 py-0.5 bg-white text-slate-600"
        >
          <option value="VIEW">View</option>
          <option value="EDIT">Edit</option>
        </select>
      )}
      {onRemove && (
        <button onClick={onRemove} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-red-600">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}

function CollaboratorPicker({ note, onAdd, currentUserId }: {
  note: Note
  onAdd: (userId: string, access: 'VIEW' | 'EDIT') => void
  currentUserId?: string
}) {
  const queryClient = useQueryClient()
  const usersQuery = useUserOptions(null)
  const [q, setQ] = useState('')
  const [selectedAccess, setSelectedAccess] = useState<'VIEW' | 'EDIT'>('VIEW')
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const existingIds = useMemo(() => {
    const ids = new Set<string>()
    if (note.authorId && typeof note.authorId === 'object') ids.add(note.authorId._id)
    ;(note.collaborators || []).forEach((c) => ids.add(typeof c.userId === 'string' ? c.userId : c.userId?._id))
    return ids
  }, [note.authorId, note.collaborators])

  const matches = (usersQuery.data || [])
    .filter((u) => u.id !== currentUserId && !existingIds.has(u.id))
    .filter((u) => !q || u.name.toLowerCase().includes(q.toLowerCase()) || (u.email || '').toLowerCase().includes(q.toLowerCase()))

  const handlePopoverOpen = (open: boolean) => {
    setIsPopoverOpen(open)
    if (open) {
      // Refetch users to ensure we have the latest list
      queryClient.invalidateQueries({ queryKey: ['work-projects', 'members', null] })
    }
  }

  return (
    <Popover open={isPopoverOpen} onOpenChange={handlePopoverOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-emerald-700 gap-1">
          <Plus className="h-3 w-3" /> Add
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-slate-700">Add Collaborator</span>
          <Select value={selectedAccess} onValueChange={(v) => setSelectedAccess(v as 'VIEW' | 'EDIT')}>
            <SelectTrigger className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="VIEW">Can view</SelectItem>
              <SelectItem value="EDIT">Can edit</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search people..."
          className="h-8 text-xs mb-2"
        />
        {usersQuery.isLoading && <p className="text-xs text-slate-400 px-2 py-3">Loading people…</p>}
        {!usersQuery.isLoading && matches.length === 0 && <p className="text-xs text-slate-400 px-2 py-3">No people found.</p>}
        <div className="max-h-56 overflow-y-auto space-y-0.5">
          {matches.map((u) => (
            <button
              key={u.id}
              onClick={() => { onAdd(u.id, selectedAccess); setQ('') }}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 text-left"
            >
              <span className="h-6 w-6 rounded-full text-white text-[9px] font-semibold flex items-center justify-center shrink-0" style={{ backgroundColor: u.color }}>
                {u.initials}
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-medium text-slate-900 truncate">{u.name}</span>
                <span className="block text-[10px] text-slate-400 truncate">{u.department || u.email}</span>
              </span>
              <span className="ml-auto text-[10px] text-slate-400 capitalize">{selectedAccess === 'EDIT' ? 'Edit' : 'View'}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
